import { Cartesian3, Ellipsoid, IntersectionTests, Plane, ScreenSpaceEventType, } from "cesium";
import { enforceAnchorConstraint } from "../../site/solid-mesh/solid-mesh-helper";
/**
 * Updates axis helper and AABB helper based on SolidMesh.
 * Applies the same rotation as the object to show axes in the object's orientation.
 *
 * @param preview - Preview instance containing axisHelper and aabbHelper
 * @param solidMesh - SolidMesh instance
 */
function updateEditHelpers(preview, solidMesh) {
    const pivot = solidMesh.getAnchorWgs84();
    // Apply the same rotation as the object to align axes with structure orientation
    const quaternion = solidMesh.shapeConfig.quaternion;
    if (preview.axisHelper.isVisible) {
        preview.axisHelper.update(pivot, 15, quaternion);
    }
    if (preview.aabbHelper.isVisible) {
        preview.aabbHelper.update(solidMesh.shapeConfig.dimensions, solidMesh.modelMatrix);
    }
}
export class SolidMeshEditHandler {
    _cesiumPicker;
    _callback;
    _solidMeshUnderEdit;
    _cesiumViewer;
    _site;
    _handler;
    _onKeyDownFuncHandler;
    _previousLeftDownAction;
    _supportPlane;
    _wgs84AnchorPositionAtLeftDown;
    _originalLocalAnchorPosition;
    _displayRotationChangeValue;
    _displayRotationSolidMeshId;
    /**
     * Gets the SolidMesh currently being edited
     */
    get solidMeshUnderEdit() {
        return this._solidMeshUnderEdit;
    }
    /**
     * Returns the last delta rotation set via rotateSolidMeshFromBaseline,
     * or undefined if cleared (edit start/end or absolute rotation).
     */
    get displayRotationDegrees() {
        if (!this._displayRotationSolidMeshId || !this._displayRotationChangeValue)
            return undefined;
        return {
            solidMeshId: this._displayRotationSolidMeshId,
            rotation: this._displayRotationChangeValue,
        };
    }
    /**
     * Creates a SolidMeshEditHandler
     * @param args - Handler arguments
     * @param cesiumViewer - CesiumView instance
     * @param site - Site instance for coordinate conversion
     */
    constructor(args, cesiumViewer, site) {
        this._handler = args.handler;
        this._cesiumPicker = args.cesiumPicker;
        this._cesiumViewer = cesiumViewer;
        this._site = site;
    }
    /**
     * Starts edit mode for the specified SolidMesh
     * @param solidMesh - SolidMesh to edit
     * @param callback - Callback for edit events
     */
    start(solidMesh, callback) {
        this._solidMeshUnderEdit = solidMesh;
        this._callback = callback;
        this.clearDisplayRotationDegrees();
        try {
            this._previousLeftDownAction = this._handler.getInputAction(ScreenSpaceEventType.LEFT_DOWN);
            // Create support plane as WGS84 tangent plane at anchor
            // Z correction is handled in onLeftUp, so simple tangent plane suffices
            const wgs84Anchor = solidMesh.getAnchorWgs84();
            const planeNormal = Ellipsoid.WGS84.geodeticSurfaceNormal(wgs84Anchor);
            this._supportPlane = Plane.fromPointNormal(wgs84Anchor, planeNormal);
            // Show helpers with same rotation as object for structure-aligned orientation
            const shapeConfig = this.solidMeshUnderEdit.shapeConfig;
            const quaternion = shapeConfig.quaternion;
            this._cesiumViewer.preview.axisHelper.show(wgs84Anchor, 15, quaternion);
            this._cesiumViewer.preview.aabbHelper.show(shapeConfig.dimensions, solidMesh.modelMatrix);
            this._handler.setInputAction(this.onLeftDown.bind(this), ScreenSpaceEventType.LEFT_DOWN);
            this._onKeyDownFuncHandler = this.onHandleKeyDown.bind(this);
            document.addEventListener("keydown", this._onKeyDownFuncHandler);
        }
        catch (err) {
            this._callback?.onError?.(err);
        }
        this._callback?.onStart?.("Edit Started");
    }
    /**
     * Ends edit mode
     */
    async end() {
        try {
            this.clearAll();
        }
        catch (err) {
            this._callback?.onError?.(err);
            throw err;
        }
        await this._callback?.onFinish?.("Edit Ended");
    }
    /**
     * Cancels edit mode
     */
    cancel() {
        this.clearAll();
        this._callback?.onCancel?.();
    }
    clearAll() {
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
        this._previousLeftDownAction &&
            this._handler.setInputAction(this._previousLeftDownAction, ScreenSpaceEventType.LEFT_DOWN);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        document.removeEventListener("keydown", this._onKeyDownFuncHandler);
        // Restore camera rotation in case clearAll is called during a drag
        this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._cesiumViewer.preview.axisHelper.hide();
        this._cesiumViewer.preview.aabbHelper.hide();
        this._cesiumViewer.viewer.scene.requestRender();
        this.clearDisplayRotationDegrees();
        this._solidMeshUnderEdit = undefined;
    }
    onHandleKeyDown(event) {
        switch (event.key) {
            case "Esc":
            case "Escape":
                this.cancel();
                break;
            default:
        }
    }
    async onLeftDown(e) {
        const pickedEntity = this._cesiumPicker.getRenderedEntity(e.position);
        if (!this._solidMeshUnderEdit)
            return;
        // Re-resolve current mesh from site in case it was replaced while open
        const current = this._site.findSolidMesh(this._solidMeshUnderEdit.id);
        if (!current)
            return;
        if (pickedEntity?.id !== current.id)
            return;
        this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = false;
        const mesh = current;
        const leftDownPosition = e.position;
        // Store position at left down (anchor point calculated from center)
        this._wgs84AnchorPositionAtLeftDown = mesh.getAnchorWgs84().clone();
        // Store original local anchor for Z correction
        // Convert WGS84 anchor to site CRS to get accurate Z (considering rotation)
        const wgs84Anchor = mesh.getAnchorWgs84();
        const localAnchors = await this._site.convertPointsToLocalSpace([
            wgs84Anchor,
        ]);
        this._originalLocalAnchorPosition = localAnchors[0];
        this._handler.setInputAction(this.onMouseMoveToTranslate.bind(this, leftDownPosition, this._wgs84AnchorPositionAtLeftDown), ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.setInputAction(this.onLeftUp.bind(this), ScreenSpaceEventType.LEFT_UP);
    }
    onMouseMoveToTranslate(leftDownPosition, wgs84PositionAtLeftDown, e) {
        if (!this._solidMeshUnderEdit)
            return;
        // Ensure we operate on the latest SolidMesh instance in the Site
        const latest = this._site.findSolidMesh(this._solidMeshUnderEdit.id);
        if (!latest)
            return;
        if (latest !== this._solidMeshUnderEdit) {
            // Replace reference so subsequent operations use the newest object
            this._solidMeshUnderEdit = latest;
        }
        const viewerCamera = this._cesiumViewer.viewerCamera;
        const startRay = viewerCamera.getPickRay(leftDownPosition);
        const start = IntersectionTests.rayPlane(startRay, this._supportPlane);
        const endRay = viewerCamera.getPickRay(e.endPosition);
        const end = IntersectionTests.rayPlane(endRay, this._supportPlane);
        if (!end || !start)
            return;
        const diff = Cartesian3.subtract(end, start, new Cartesian3());
        if (Cartesian3.magnitude(diff) === 0)
            return;
        const newWgs84Anchor = Cartesian3.add(wgs84PositionAtLeftDown, diff, new Cartesian3());
        // Per-frame drag preview: rigid ECEF translate, localCenter settles on LEFT_UP
        this._solidMeshUnderEdit.translatePreviewWgs84(Cartesian3.subtract(newWgs84Anchor, this._solidMeshUnderEdit.getAnchorWgs84(), new Cartesian3()));
        updateEditHelpers(this._cesiumViewer.preview, this._solidMeshUnderEdit);
        this._cesiumViewer.viewer.scene.requestRender();
        this._callback?.onProgress?.("Translating");
    }
    async onLeftUp(e) {
        this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        try {
            // Z correction: WGS84 plane ≠ site CRS Z constant for large movements
            if (this._solidMeshUnderEdit && this._originalLocalAnchorPosition) {
                // Get current WGS84 anchor
                const currentWgs84Anchor = this._solidMeshUnderEdit.getAnchorWgs84();
                // Convert anchor to site CRS
                const siteCrsAnchors = await this._site.convertPointsToLocalSpace([
                    currentWgs84Anchor,
                ]);
                const currentSiteCrsAnchor = siteCrsAnchors[0];
                // Replace Z with original Z (preserve floor height)
                const correctedSiteCrsAnchor = new Cartesian3(currentSiteCrsAnchor.x, currentSiteCrsAnchor.y, this._originalLocalAnchorPosition.z);
                // Settle at the target (also clears the stale localCenter left by
                // the drag preview and rebuilds the local frame there)
                await this._solidMeshUnderEdit.translateToLocalAnchor({
                    targetLocalAnchor: correctedSiteCrsAnchor,
                });
                updateEditHelpers(this._cesiumViewer.preview, this._solidMeshUnderEdit);
            }
        }
        catch (err) {
            this._callback?.onError?.(err);
        }
        this._cesiumViewer.viewer.scene.requestRender();
        this._callback?.onProgress?.("Operation Ended");
    }
    /**
     * Rotates the SolidMesh being edited
     * @param quaternion - Rotation quaternion
     */
    rotateSolidMesh(quaternion) {
        if (this._solidMeshUnderEdit) {
            this.clearDisplayRotationDegrees();
            this._solidMeshUnderEdit.rotate(quaternion);
            enforceAnchorConstraint(this._solidMeshUnderEdit);
            updateEditHelpers(this._cesiumViewer.preview, this._solidMeshUnderEdit);
            this._cesiumViewer.viewer.scene.requestRender();
        }
    }
    /**
     * Scales the SolidMesh being edited
     * @param scale - Scale values for each axis
     */
    scaleSolidMesh(scale) {
        if (this._solidMeshUnderEdit) {
            this._solidMeshUnderEdit.scale(scale);
            enforceAnchorConstraint(this._solidMeshUnderEdit);
            updateEditHelpers(this._cesiumViewer.preview, this._solidMeshUnderEdit);
            this._cesiumViewer.viewer.scene.requestRender();
        }
    }
    /**
     * Refreshes edit helpers (pivot axes, AABB, drag plane) from current state
     * WITHOUT applying anchor constraint. Use after an atomic state apply where
     * constraints were already satisfied at computation time.
     */
    refreshEditHelpers() {
        if (!this._solidMeshUnderEdit)
            return;
        const wgs84Anchor = this._solidMeshUnderEdit.getAnchorWgs84();
        const planeNormal = Ellipsoid.WGS84.geodeticSurfaceNormal(wgs84Anchor);
        this._supportPlane = Plane.fromPointNormal(wgs84Anchor, planeNormal);
        updateEditHelpers(this._cesiumViewer.preview, this._solidMeshUnderEdit);
        this._cesiumViewer.viewer.scene.requestRender();
    }
    /**
     * Hides or restores the edit helpers without ending the active edit session.
     */
    setVisibility(visible) {
        if (!this._solidMeshUnderEdit)
            return;
        if (visible) {
            const shapeConfig = this._solidMeshUnderEdit.shapeConfig;
            this._cesiumViewer.preview.axisHelper.show(this._solidMeshUnderEdit.getAnchorWgs84(), 15, shapeConfig.quaternion);
            this._cesiumViewer.preview.aabbHelper.show(shapeConfig.dimensions, this._solidMeshUnderEdit.modelMatrix);
        }
        else {
            this._cesiumViewer.preview.axisHelper.hide();
            this._cesiumViewer.preview.aabbHelper.hide();
        }
        this._cesiumViewer.viewer.scene.requestRender();
    }
    /**
     * Refreshes anchor-dependent helpers (pivot + drag plane).
     */
    refreshAnchorReference() {
        if (!this._solidMeshUnderEdit)
            return;
        enforceAnchorConstraint(this._solidMeshUnderEdit);
        // Recompute drag plane after constraint may have moved the mesh
        const wgs84Anchor = this._solidMeshUnderEdit.getAnchorWgs84();
        const planeNormal = Ellipsoid.WGS84.geodeticSurfaceNormal(wgs84Anchor);
        this._supportPlane = Plane.fromPointNormal(wgs84Anchor, planeNormal);
        updateEditHelpers(this._cesiumViewer.preview, this._solidMeshUnderEdit);
        this._cesiumViewer.viewer.scene.requestRender();
    }
    /**
     * Sets anchor type for the mesh currently under edit.
     */
    setAnchorType(anchorType) {
        if (!this._solidMeshUnderEdit)
            return;
        this._solidMeshUnderEdit.updateAttributes({ anchorType });
        this.refreshAnchorReference();
    }
    /**
     * Stores the display-facing delta rotation for the specified solid mesh.
     * @param rotation - Delta rotation in degrees (XYZ)
     * @param solidMeshId - Target solid mesh ID
     */
    setDisplayRotationChangeValue(rotation, solidMeshId) {
        this._displayRotationChangeValue = {
            x: rotation.x,
            y: rotation.y,
            z: rotation.z,
        };
        this._displayRotationSolidMeshId = solidMeshId;
    }
    /**
     * Clears the cached display rotation. Called on edit start/end and absolute rotation.
     * @param solidMeshId - When given, clears only if the cache belongs to this mesh
     */
    clearDisplayRotationDegrees(solidMeshId) {
        if (solidMeshId !== undefined &&
            this._displayRotationSolidMeshId !== undefined &&
            this._displayRotationSolidMeshId !== solidMeshId) {
            return;
        }
        this._displayRotationChangeValue = undefined;
        this._displayRotationSolidMeshId = undefined;
    }
}
//# sourceMappingURL=solid-mesh-edit-handler.js.map